import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, normalizeLocale } from '@evi/shared';

/**
 * i18next foundation for the driver PWA (the i18n epic). English is the source
 * language and the guaranteed fallback for any missing key, so partially
 * translated locales degrade to English per-key rather than showing raw keys.
 *
 * Resource discovery is automatic: every `src/locales/<locale>/<namespace>.json`
 * is eagerly bundled via Vite's import.meta.glob and keyed by locale + file
 * name. Adding a page's translations is therefore zero-config — drop the eight
 * JSON files in and they load; no registration here. Init is synchronous
 * (bundled, not lazy-loaded), so no <Suspense> boundary is needed.
 *
 * Legal copy is NEVER translated (Terms / disclaimers stay English) and is not
 * routed through i18next.
 */
const LOCALE_STORAGE_KEY = 'evi.locale';

// Eager glob: path -> { default: <json> }. Vite inlines every matching file.
const localeModules = import.meta.glob<{ default: Record<string, unknown> }>(
  '../locales/*/*.json',
  { eager: true },
);

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
const namespaceSet = new Set<string>();
for (const [path, mod] of Object.entries(localeModules)) {
  // ../locales/<locale>/<namespace>.json
  const match = path.match(/\/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, locale, namespace] = match;
  if (!locale || !namespace) continue;
  (resources[locale] ??= {})[namespace] = mod.default;
  namespaceSet.add(namespace);
}
const namespaces = [...namespaceSet];

/**
 * Pre-auth starting language: a previously chosen locale wins; otherwise the
 * device language (coerced to a supported one); otherwise English. Once the
 * driver authenticates, the server-side preference overrides this via
 * useLocaleSync.
 */
function initialLocale(): string {
  try {
    const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) return normalizeLocale(stored);
  } catch {
    /* private mode / no storage */
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    return normalizeLocale(navigator.language);
  }
  return DEFAULT_LOCALE;
}

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: [...SUPPORTED_LOCALES],
  defaultNS: 'common',
  ns: namespaces.length ? namespaces : ['common'],
  interpolation: { escapeValue: false }, // React already escapes
  returnNull: false,
  react: { useSuspense: false },
});

export { LOCALE_STORAGE_KEY };
export default i18n;
